import React, { useEffect, useMemo, useState } from 'react';
import * as db from '../services/db';
import { AuditEvent } from '../types';
import './Sections.css';

// Audit Log (Android EventLog / Event bean) — a chronological activity trail of
// sales, voids, edits, logins, shift closes and settings changes. Events are
// appended by the data layer (db.logEvent); this screen reads and filters them.
const TYPES = ['all', 'sale', 'void', 'edit', 'login', 'shift_close', 'settings'];

const AuditLog: React.FC = () => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');

  const refresh = () => {
    db.getAuditEvents().then(setEvents);
  };
  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return [...events]
      .reverse()
      .filter((e) => (type === 'all' || e.type === type) && (!t || e.detail.toLowerCase().includes(t)));
  }, [events, type, search]);

  const exportCsv = () => {
    const rows = [['Time', 'Type', 'Detail', 'User']];
    filtered.forEach((e) => rows.push([new Date(e.timestamp).toLocaleString(), e.type, e.detail, e.userName ?? '']));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-log.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    if (window.confirm('Clear the entire audit log? This cannot be undone.')) {
      db.saveAuditEvents([]);
      refresh();
    }
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>Audit Log</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-teal" onClick={refresh}>Refresh</button>
          <button className="btn-teal" onClick={exportCsv} disabled={filtered.length === 0}>CSV</button>
          <button className="btn-red" onClick={clearAll} disabled={events.length === 0}>Clear</button>
        </div>
      </div>

      <div className="section-card">
        <div className="field-row" style={{ marginBottom: 12 }}>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => (
              <option key={t} value={t}>{t === 'all' ? 'All types' : t}</option>
            ))}
          </select>
          <input placeholder="Search detail" value={search} onChange={(e) => setSearch(e.target.value)} />
          <span className="muted">{filtered.length} event{filtered.length === 1 ? '' : 's'}</span>
        </div>

        <table className="section-table">
          <thead>
            <tr>
              <th style={{ width: 170 }}>Time</th>
              <th style={{ width: 110 }}>Type</th>
              <th>Detail</th>
              <th style={{ width: 120 }}>User</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.timestamp).toLocaleString()}</td>
                <td>{e.type}</td>
                <td>{e.detail}</td>
                <td>{e.userName ?? '—'}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="muted" style={{ padding: 16 }}>No events.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLog;
